import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Provide a dummy object as default to prevent destructuring errors
const AuthContext = createContext({
    doctor: null,
    user: null,
    login: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
    logout: () => { },
    isAuthenticated: false,
    fetchOrdersByStatus: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
    saveLetter: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
    completeOrder: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
    reopenOrder: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
    regenerateDraft: () => Promise.resolve({ success: false, error: 'Auth not initialized' }),
});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('doctorToken');
        setDoctor(null);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('doctorToken');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                if (decoded.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    setDoctor(decoded);
                }
            } catch (e) {
                logout();
            }
        }
        setLoading(false);
    }, [logout]);

    const login = useCallback(async (email, password) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/login`, { email, password });
            if (res.data.token) {
                localStorage.setItem('doctorToken', res.data.token);
                setDoctor(jwtDecode(res.data.token));
                return { success: true };
            }
            return { success: false, error: 'Token not received' };
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Login failed' };
        }
    }, []);

    const fetchOrdersByStatus = useCallback(async (status) => {
        try {
            const token = localStorage.getItem('doctorToken');
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders`, {
                params: { status },
                headers: { Authorization: `Bearer ${token}` }
            });
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Failed to fetch orders' };
        }
    }, []);

    const saveLetter = useCallback(async (orderId, content) => {
        try {
            const token = localStorage.getItem('doctorToken');
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/save-letter`,
                { content },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Failed to save letter' };
        }
    }, []);

    const completeOrder = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem('doctorToken');
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/complete`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Failed to complete order' };
        }
    }, []);

    const reopenOrder = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem('doctorToken');
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/reopen`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Failed to reopen order' };
        }
    }, []);

    const regenerateDraft = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem('doctorToken');
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/regenerate-draft`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.error || 'Failed to regenerate draft' };
        }
    }, []);


    const value = useMemo(() => ({
        doctor,
        user: doctor,
        login,
        logout,
        isAuthenticated: !!doctor,
        fetchOrdersByStatus,
        saveLetter,
        completeOrder,
        reopenOrder,
        regenerateDraft
    }), [doctor, login, logout, fetchOrdersByStatus, saveLetter, completeOrder, reopenOrder, regenerateDraft]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};